import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { getApiUrl } from '../utils/apiUrl';
import { DEMO_MODE, demoWorkOrders } from '../services/demoData';

const WorkOrdersWidget = ({ onClick }) => {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  const getToken = () => localStorage.getItem('token');

  // Get current user info from localStorage
  const getCurrentUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        return JSON.parse(userStr);
      }
    } catch (e) {
      console.error('[WorkOrdersWidget] Error parsing user:', e);
    }
    return null;
  };

  // Check if user is in Fakturering team
  const isInInvoicingTeam = () => {
    return teams.some(team => team.name?.toLowerCase().includes('fakturering'));
  };

  // Check if user is Verkstedforman
  const isVerkstedforman = () => {
    const roleName = currentUser?.roleName?.toLowerCase() || '';
    return roleName.includes('verkstedforman') || roleName.includes('verksted');
  };

  // Check if user is Prosjektleder
  const isProsjektleder = () => {
    const roleName = currentUser?.roleName?.toLowerCase() || '';
    return roleName === 'prosjektleder' || roleName === 'prosjekleder';
  };

  // Check if user is member of any team in this order
  const isUserMemberOfOrder = (order) => {
    if (!order.teams || !teams || teams.length === 0) return false;
    const myTeamIds = teams.map(t => t.id);
    return order.teams.some(team => myTeamIds.includes(team.id));
  };

  // Check if user's team(s) are finished on this order
  const isMyTeamFinished = (order) => {
    if (!order.teams || !teams || teams.length === 0) return false;
    const myTeamIds = teams.map(t => t.id);
    const myTeamsOnOrder = order.teams.filter(team => myTeamIds.includes(team.id));
    if (myTeamsOnOrder.length === 0) return false;
    return myTeamsOnOrder.every(team => team.status_name === 'Ferdig');
  };

  // Apply OrdersFlowWidget 'mine-active' filter
  const applyMineActiveFilter = (orders) => {
    if (!orders || orders.length === 0) return [];
    if (!currentUser) return [];

    // SPECIAL: Fakturering team sees ALL orders that have Fakturering team assigned
    if (isInInvoicingTeam()) {
      return orders.filter(order => {
        const invoicingTeam = order.teams?.find(t => 
          t.name?.toLowerCase().includes('fakturering')
        );
        if (!invoicingTeam) return false;
        const invoicingStatus = invoicingTeam.status_name?.toLowerCase();
        return invoicingStatus !== 'fakturert' && invoicingStatus !== 'ferdig';
      });
    }

    // SPECIAL: Verkstedforman sees ALL non-completed orders
    if (isVerkstedforman()) {
      return orders.filter(order => order.status?.toLowerCase() !== 'fullført');
    }

    // Prosjektleder sees orders they created (not completed)
    if (isProsjektleder()) {
      return orders.filter(order => 
        order.createdBy === currentUser.id && order.status?.toLowerCase() !== 'fullført'
      );
    }

    // Default: Teammedlem (Operatør/Forman)
    return orders.filter(order => {
      // Ordre uten ordreansvarlig som ikke er fullført
      if (!order.createdBy && order.status?.toLowerCase() !== 'fullført') {
        return true;
      }
      // Ordre jeg er ansvarlig for som ikke er fullført
      if (order.createdBy === currentUser.id && order.status?.toLowerCase() !== 'fullført') {
        return true;
      }
      // Ordre hvor jeg er medlem og mitt team ikke er ferdig
      return isUserMemberOfOrder(order) && !isMyTeamFinished(order);
    });
  };

  // Fetch user teams
  const fetchMyTeams = async () => {
    if (DEMO_MODE.enabled || localStorage.getItem('demoMode') === 'true') {
      return;
    }
    
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/teams/my-teams`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTeams(data || []);
      }
    } catch (err) {
      console.error('[WorkOrdersWidget] Error fetching teams:', err);
    }
  };

  // Hent arbeidsordrer - viser filtrerte ordrer (mine-active), sortert etter dato
  const fetchWorkOrders = async () => {
    // Check for demo mode
    if (DEMO_MODE.enabled || localStorage.getItem('demoMode') === 'true') {
      const filtered = applyMineActiveFilter(demoWorkOrders);
      const sorted = [...filtered].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setWorkOrders(sorted.slice(0, 5));
      setLoading(false);
      return;
    }
    
    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/work-orders`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Apply mine-active filter
        const filtered = applyMineActiveFilter(data);
        // Sort by date (newest first) and take first 5
        const sorted = filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setWorkOrders(sorted.slice(0, 5));
      }
    } catch (err) {
      console.error('[WorkOrdersWidget] Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initialize: load current user and teams first
  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
    fetchMyTeams();
  }, []);

  // Fetch work orders when teams and user are loaded
  useEffect(() => {
    if (currentUser && (teams.length > 0 || DEMO_MODE.enabled || localStorage.getItem('demoMode') === 'true')) {
      fetchWorkOrders();
      const interval = setInterval(fetchWorkOrders, 60000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams, currentUser]);

  // Status farge
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'ny':
        return 'primary';
      case 'pågår':
        return 'warning';
      case 'fullført':
        return 'success';
      case 'avbrutt':
        return 'error';
      default:
        return 'default';
    }
  };

  // Status label
  const getStatusLabel = (status) => {
    return status?.charAt(0).toUpperCase() + status?.slice(1) || 'Ukjent';
  };

  return (
    <Card 
      elevation={3} 
      sx={{ 
        width: '100%',
        minHeight: 220,
        height: '100%',
        cursor: 'pointer',
        transition: 'all 0.3s',
        '&:hover': {
          elevation: 6,
          transform: 'translateY(-4px)'
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon color="primary" fontSize="large" />
            <Typography variant="h6" fontWeight="bold">
              Arbeidsordrer
            </Typography>
          </Box>
          <IconButton size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Laster...
            </Typography>
          </Box>
        ) : workOrders.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <AssignmentIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Ingen arbeidsordrer
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {workOrders.map((order) => (
              <ListItem
                key={order.id}
                disablePadding
                sx={{ 
                  mb: 1,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  px: 1
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" fontWeight="500" noWrap sx={{ flex: 1, mr: 1 }}>
                        #{order.orderNumber}
                      </Typography>
                      <Chip 
                        label={getStatusLabel(order.status)} 
                        size="small" 
                        color={getStatusColor(order.status)}
                      />
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {order.customer || 'Ingen kunde'}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Trykk for å se alle
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default WorkOrdersWidget;
